import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import BusinessEditor from './BusinessEditor';
import ConnectSheet from './ConnectSheet';
import { AiConnectForm, HelperGuestScreen, HelperInvitePanel } from './AiConnect';
import { timeLabel, useApp, stampLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { setupSteps } from '../store/setup';
import type { StepInfo } from '../store/setup';
import { DEMO_TODAY } from '../store/seed';
import type { Account, SetupStep } from '../store/seed';
import type { Post } from '../types';
import { aiProviderName } from './aiProviders';

const NARROW_SCREEN_WIDTH = 820;
const SCROLL_AFTER_RENDER_DELAY_MS = 60;
const NOVEMBER = 10;
const DAYS_UNTIL_FIRST_BATCH = 2;
const DEFAULT_ALWAYS_ON_POSTS_PER_WEEK = 4;

const stepStatusClass: Record<StepInfo['state'], string> = {
  done: 'published',
  later: 'neutral',
  waiting: 'review',
  todo: 'planned',
};

const isPersonalAccount = (account: Account) => /personal/i.test(account.type);

const accountHealthStatusClass = (account: Account) => {
  if (account.health === 'Connected') return 'published';
  if (account.health === 'Needs reconnecting') return 'review';
  return 'neutral';
};

const postStatusClass = (post: Post) => {
  if (post.status === 'Needs your review') return 'review';
  if (post.status === 'Scheduled') return 'scheduled';
  return 'neutral';
};

function scrollOpenStepIntoViewOnNarrowScreens() {
  if (window.innerWidth >= NARROW_SCREEN_WIDTH) return;
  window.setTimeout(
    () =>
      document
        .querySelector('.onboarding-step')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    SCROLL_AFTER_RENDER_DELAY_MS
  );
}

export default function OnboardingPage({ onBack }: { onBack: () => void }) {
  const { translate, readOnlyProps, readOnly, language } = useApp();
  const { route, navigate, notify, actor, startReview, role } = useUi();
  const { commands } = useDemo();
  const workspaceData = useWorkspaceData(route.workspaceId);
  const { workspace, brand, assets, aiConnection, accounts, posts, onboarding, helpers } =
    workspaceData;
  const steps = setupSteps(workspaceData);
  const openStepId =
    (route.id as SetupStep | undefined) ?? steps.find((step) => step.state === 'todo')?.id ?? null;
  const openStep = (id: SetupStep | null) => {
    navigate({ page: 'onboarding', id: id ?? undefined }, { replace: true });
    scrollOpenStepIntoViewOnNarrowScreens();
  };
  const withLocalisedDate = (variables?: Record<string, string>) =>
    variables && {
      ...variables,
      ...(variables.date ? { date: timeLabel(variables.date, language, translate) } : {}),
    };
  const [accountToConnect, setAccountToConnect] = useState<Account | 'new' | null>(null);
  const [guestHelperId, setGuestHelperId] = useState<string | null>(null);
  const doneCount = steps.filter((step) => step.state === 'done').length;
  const percentDone = Math.round((doneCount / steps.length) * 100);
  const currentStep = steps.find((step) => step.id === openStepId);
  const isOwnerOrAgency = role === 'owner' || role === 'agency';
  const formatDayOfNovember = (dayOfNovember: number) =>
    new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short' }).format(
      new Date(2026, NOVEMBER, dayOfNovember)
    );
  const stateLabel = (step: StepInfo) => {
    if (step.state === 'done') return translate('Done');
    if (step.state === 'later') return translate('Skipped for now');
    if (step.state === 'waiting') return translate('Waiting for someone else');
    return translate('To do · {time}', { time: translate(step.time) });
  };
  const nextUnfinishedStepAfter = (from: SetupStep) => {
    const fromIndex = steps.findIndex((step) => step.id === from);
    return steps
      .slice(fromIndex + 1)
      .concat(steps.slice(0, fromIndex))
      .find((step) => step.state === 'todo' || step.state === 'later');
  };
  const openNextUnfinishedStepAfter = (from: SetupStep) => {
    const nextStep = nextUnfinishedStepAfter(from);
    if (nextStep) openStep(nextStep.id);
  };
  const skipStepForNow = (id: SetupStep) => {
    commands.setSetupLater(workspace.id, id, true);
    notify(
      translate('{x}: skipped for now. It stays on this list.', {
        x: translate(steps.find((step) => step.id === id)!.name),
      })
    );
    const nextStep = nextUnfinishedStepAfter(id);
    openStep(nextStep && nextStep.id !== id ? nextStep.id : null);
  };

  const activationBlockers: string[] = [];
  if (aiConnection.status !== 'connected')
    activationBlockers.push(translate('Connect AI so AMY can draft.'));
  if (steps.find((step) => step.id === 'samples')!.state !== 'done')
    activationBlockers.push(translate('Review the sample posts first.'));
  const businessAccounts = accounts.filter((account) => !isPersonalAccount(account));
  const connectedAccounts = businessAccounts.filter((account) => account.health === 'Connected');
  const disconnectedAccounts = businessAccounts.filter((account) => account.health !== 'Connected');
  if (connectedAccounts.length === 0)
    activationBlockers.push(translate('Connect at least one social account.'));
  const alwaysOnCampaign = workspaceData.campaigns.find(
    (campaign) => campaign.goal === 'always-on'
  );
  const firstBatchDay = DEMO_TODAY + DAYS_UNTIL_FIRST_BATCH;
  const samplePosts = posts.filter((post) => onboarding.samples.includes(post.id));
  const samplePostsNeedingReview = samplePosts.filter(
    (post) => post.status === 'Needs your review'
  );
  const hasPendingHelperInvite = helpers.some((helper) => helper.status === 'Pending');

  const connectButtonLabel = (account: Account) => {
    if (isPersonalAccount(account))
      return account.owner === actor.name
        ? translate('Connect my profile')
        : translate('Invite to connect');
    return account.health === 'Needs reconnecting' ? translate('Reconnect') : translate('Connect');
  };

  const stepBody = (step: StepInfo) => {
    switch (step.id) {
      case 'business':
        return <BusinessEditor compact onSaved={() => openNextUnfinishedStepAfter('business')} />;
      case 'brand':
        return (
          <div>
            <p>
              {translate(
                'Your brand kit is version {v}, approved by {name}. AMY writes and designs with it.',
                {
                  v: brand.version,
                  name: brand.history[brand.history.length - 1]?.by ?? workspace.owner,
                }
              )}
            </p>
            <div className="swatch-row">
              {brand.active.colours?.slice(0, 5).map((colour) => (
                <span
                  key={colour.hex}
                  className="swatch"
                  style={{ background: colour.hex }}
                  title={`${colour.name} ${colour.hex}`}
                />
              ))}
            </div>
            <div className="button-row">
              <button className="button primary" onClick={() => navigate({ page: 'brand' })}>
                <SafeIcon name="FiDroplet" />
                {translate('Open the brand kit')}
              </button>
            </div>
          </div>
        );
      case 'materials':
        return (
          <div>
            <p>
              {translate(
                '{n} photos and videos are in Materials. AMY picks from them; add more any time.',
                { n: assets.filter((asset) => !asset.archived).length }
              )}
            </p>
            <div className="thumb-row">
              {assets
                .filter((asset) => asset.image && !asset.archived)
                .slice(0, 6)
                .map((asset) => (
                  <img key={asset.id} src={asset.image} alt={asset.name} />
                ))}
            </div>
            <div className="button-row">
              <button className="button primary" onClick={() => navigate({ page: 'materials' })}>
                <SafeIcon name="FiImage" />
                {translate('Open Materials')}
              </button>
            </div>
          </div>
        );
      case 'ai':
        return (
          <div>
            {aiConnection.status === 'connected' ? (
              <div className="notice-banner ok">
                <SafeIcon name="FiCheckCircle" />
                <span>
                  {translate('Connected to {provider} ({model}) by {name}. Last test {when}.', {
                    provider: aiProviderName(aiConnection.provider),
                    model: translate(aiConnection.model ?? ''),
                    name: aiConnection.connectedBy ?? '',
                    when: stampLabel(aiConnection.lastTest ?? '', language),
                  })}
                </span>
              </div>
            ) : (
              <p>
                {translate(
                  'An API key lets AMY use your own AI account. Your AI provider bills you separately from AMY.'
                )}
                {aiConnection.trial
                  ? ` ${translate('Until then AMY drafts on Hakusana’s trial allowance, which is not billed to you.')}`
                  : ''}
              </p>
            )}
            {isOwnerOrAgency && aiConnection.status !== 'connected' && !hasPendingHelperInvite && (
              <AiConnectForm onConnected={() => openNextUnfinishedStepAfter('ai')} />
            )}
            {isOwnerOrAgency && aiConnection.status !== 'connected' && (
              <HelperInvitePanel onGuest={setGuestHelperId} />
            )}
            {!isOwnerOrAgency && (
              <p className="muted">
                {translate('Only {name} can connect AI.', { name: workspace.owner })}
              </p>
            )}
            {aiConnection.status === 'connected' && (
              <button className="button" onClick={() => navigate({ page: 'settings', id: 'ai' })}>
                <SafeIcon name="FiSettings" />
                {translate('Manage in Settings')}
              </button>
            )}
          </div>
        );
      case 'social':
        return (
          <div>
            {accounts.map((account) => {
              const personal = isPersonalAccount(account);
              const canConnect = isOwnerOrAgency || (personal && account.owner === actor.name);
              return (
                <div className="checklist-item" key={account.id}>
                  <SafeIcon name="FiLink" />
                  <div style={{ flex: 1 }}>
                    <strong>{account.name}</strong>
                    <div className="muted">
                      {account.network} · {translate(account.type)}
                      {personal ? ` · ${translate('optional')}` : ''}
                    </div>
                  </div>
                  <span className={`status ${accountHealthStatusClass(account)}`}>
                    {translate(account.health)}
                  </span>
                  {account.health !== 'Connected' &&
                    (canConnect ? (
                      <button
                        className="button small primary"
                        onClick={() => setAccountToConnect(account)}
                        {...readOnlyProps}
                      >
                        {connectButtonLabel(account)}
                      </button>
                    ) : (
                      <span className="muted small-print">
                        {translate('Only {name} can reconnect this account', {
                          name: account.owner,
                        })}
                      </span>
                    ))}
                </div>
              );
            })}
            {isOwnerOrAgency && (
              <button
                className="button"
                onClick={() => setAccountToConnect('new')}
                {...readOnlyProps}
              >
                <SafeIcon name="FiPlus" />
                {translate('Connect another account')}
              </button>
            )}
            <p className="muted small-print">
              {translate(
                'Personal LinkedIn profiles are optional and connected by their owner; they do not block setup.'
              )}
            </p>
          </div>
        );
      case 'samples':
        return (
          <div>
            <p>
              {translate(
                'AMY made {n} sample posts with your brand kit. Approve them or ask for changes; this shows AMY what you like.',
                { n: samplePosts.length }
              )}
            </p>
            <div className="sample-grid">
              {samplePosts.map((post) => (
                <button
                  key={post.id}
                  className="sample-card"
                  onClick={() => navigate({ page: 'post', id: post.id })}
                >
                  {post.image && <img src={post.image} alt="" />}
                  <strong>{post.title}</strong>
                  <span className="muted small-print">
                    {post.channel} · {formatDayOfNovember(post.day)}
                  </span>
                  <span className={`status ${postStatusClass(post)}`}>
                    {translate(post.status)}
                  </span>
                </button>
              ))}
            </div>
            {samplePostsNeedingReview.length > 0 && (
              <button
                className="button primary"
                onClick={() =>
                  startReview(
                    undefined,
                    samplePostsNeedingReview.map((post) => post.id)
                  )
                }
              >
                <SafeIcon name="FiEye" />
                {translate('Review the samples')}
              </button>
            )}
          </div>
        );
      case 'activate':
        return (
          <div>
            <dl className="activate-summary">
              <dt>{translate('Channels')}</dt>
              <dd>
                {connectedAccounts.map((account) => account.network).join(', ') || '—'}
                {disconnectedAccounts.length > 0 && (
                  <span className="field-warn">
                    {' '}
                    ·{' '}
                    {translate('{list} not connected yet, left out until connected', {
                      list: disconnectedAccounts.map((account) => account.network).join(', '),
                    })}
                  </span>
                )}
              </dd>
              <dt>{translate('Cadence')}</dt>
              <dd>
                {translate(
                  'Up to one post per channel per day; about {n} a week from the always-on plan',
                  {
                    n: alwaysOnCampaign?.perWeek ?? DEFAULT_ALWAYS_ON_POSTS_PER_WEEK,
                  }
                )}
              </dd>
              <dt>{translate('Brand kit')}</dt>
              <dd>{translate('Version {v}', { v: brand.version })}</dd>
              <dt>{translate('First batch')}</dt>
              <dd>
                {translate(
                  'AMY drafts the first posts for {date}; you review them before anything is scheduled.',
                  { date: formatDayOfNovember(firstBatchDay) }
                )}
              </dd>
            </dl>
            {onboarding.activated && (
              <div className="notice-banner ok">
                <SafeIcon name="FiCheckCircle" />
                <span>
                  {translate('Activated on {date}.', {
                    date: timeLabel(onboarding.activated, language, translate),
                  })}
                </span>
              </div>
            )}
            {!onboarding.activated && activationBlockers.length > 0 && (
              <div className="notice-banner warn">
                <SafeIcon name="FiAlertTriangle" />
                <div>
                  <strong>{translate('Before you can activate')}</strong>
                  <ul>
                    {activationBlockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {!onboarding.activated && (
              <button
                className="button primary"
                disabled={readOnly || activationBlockers.length > 0 || !isOwnerOrAgency}
                onClick={() => {
                  commands.activateSetup(workspace.id, actor);
                  notify(translate('Plan activated. AMY drafts the first batch for your review.'));
                }}
              >
                <SafeIcon name="FiPlayCircle" />
                {translate('Activate the plan')}
              </button>
            )}
          </div>
        );
    }
  };

  const stepMarker = (step: StepInfo, stepIndex: number) => {
    if (step.state === 'done') return <SafeIcon name="FiCheck" size={14} />;
    if (step.state === 'waiting') return <SafeIcon name="FiClock" size={13} />;
    return stepIndex + 1;
  };

  return (
    <div className="content-stack onboarding-page">
      <div className="page-title-row">
        <div>
          <h1>{translate('Finish setting up')}</h1>
          <p className="subtitle">
            {translate('{ws} · {n} of {total} done. Do the steps in any order.', {
              ws: workspace.name,
              n: doneCount,
              total: steps.length,
            })}
          </p>
        </div>
        <button className="button" onClick={onBack}>
          <SafeIcon name="FiArrowLeft" />
          {translate('Back to calendar')}
        </button>
      </div>
      <div className="onboarding-layout">
        <div className="panel onboarding-list">
          <div className="progress-line">
            <strong>{percentDone}%</strong>
            <div
              className="progress-track"
              style={{ flex: 1, marginTop: 0 }}
              role="progressbar"
              aria-valuenow={percentDone}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={translate('Setup progress')}
            >
              <span style={{ width: `${percentDone}%` }} />
            </div>
          </div>
          {steps.map((step, stepIndex) => (
            <button
              key={step.id}
              className={`checklist-item setup-row ${openStepId === step.id ? 'current' : ''} ${step.state}`}
              aria-current={openStepId === step.id ? 'step' : undefined}
              onClick={() => openStep(step.id)}
            >
              <span className="check-dot">{stepMarker(step, stepIndex)}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>
                <strong>{translate(step.name)}</strong>
                <span className="muted small-print setup-state">
                  {stateLabel(step)}
                  {step.note && step.state !== 'todo'
                    ? ` · ${translate(step.note, withLocalisedDate(step.variables))}`
                    : ''}
                </span>
              </span>
              <SafeIcon name="FiChevronRight" size={15} />
            </button>
          ))}
        </div>
        {currentStep && (
          <div className="panel onboarding-step" aria-live="polite">
            <div className="panel-header">
              <h2>{translate(currentStep.name)}</h2>
              <span className={`status ${stepStatusClass[currentStep.state]}`}>
                {stateLabel(currentStep)}
              </span>
            </div>
            {stepBody(currentStep)}
            {currentStep.state !== 'done' && currentStep.state !== 'waiting' && (
              <div className="step-footer">
                <button
                  className="button ghost"
                  onClick={() => skipStepForNow(currentStep.id)}
                  {...readOnlyProps}
                >
                  {translate('Do this later')}
                </button>
                <span className="muted small-print">
                  {translate('Skipped steps stay on the list and are not marked done.')}
                </span>
              </div>
            )}
          </div>
        )}
        {!currentStep && (
          <div className="panel onboarding-step">
            <h2>{translate('All set')}</h2>
            <p>{translate('Every step is done. You can open any of them again from the list.')}</p>
          </div>
        )}
      </div>
      {accountToConnect && (
        <ConnectSheet
          account={accountToConnect === 'new' ? undefined : accountToConnect}
          onClose={() => setAccountToConnect(null)}
        />
      )}
      {guestHelperId && (
        <HelperGuestScreen helperId={guestHelperId} onClose={() => setGuestHelperId(null)} />
      )}
    </div>
  );
}
